/**
 * Produces a repeatable value from 0 to 1 for an input such as a dot index.
 * Poses use it to give dots different speeds, amplitudes, or timing without those values changing between frames.
 */
export const getStableVariation = (input: number): number => {
	const value = Math.sin(input * 12.9898) * 43758.5453;

	return value - Math.floor(value);
};

/**
 * Measures how close the current loop progress is to a timed pulse.
 * Speaking beats and cursor clicks use it so a pulse remains smooth when a cycle ends and restarts.
 */
export const getWrappedCycleDistance = (value: number, target: number): number => {
	const distance = Math.abs(value - target);

	return Math.min(distance, 1 - distance);
};

/**
 * Converts elapsed time into progress through one fixed-duration animation cycle.
 * Speaking and cursor poses use it to schedule repeating voice beats and clicks.
 */
export const getCycleProgress = (timeSeconds: number, durationSeconds: number): number =>
	(timeSeconds % durationSeconds) / durationSeconds;

/**
 * Keeps only the position within the current loop after earlier loops have completed.
 * Agent comets and TWG flashes use it to travel through their dot sequences.
 */
export const getRepeatingProgress = (value: number): number => value - Math.floor(value);

/** Converts Math.sin(), whose output ranges from -1 to 1, into a 0-to-1 animation strength. */
export const getSineWaveStrength = (angleRadians: number): number =>
	0.5 + 0.5 * Math.sin(angleRadians);

/**
 * Turns a pose's activity signal into a localized glow spark.
 * A larger concentration power makes the spark shorter and limits how many dots glow together.
 */
export const getGlowSparkStrength = (
	activityStrength: number,
	concentrationPower: number,
	maximumStrength = 1,
): number => Math.max(0, Math.min(1, activityStrength)) ** concentrationPower * maximumStrength;
